import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert, AlertIcon, Box, Button, Flex, Grid, HStack, Input, InputGroup, InputLeftElement, Select,
  Table, Tbody, Td, Text, Th, Thead, Tooltip, Tr, VStack, Wrap, WrapItem, useMediaQuery,
} from "@chakra-ui/react";
import { RefreshCw, Search, SlidersHorizontal, X } from "lucide-react";
import { getSaudeAnunciosItens } from "../../services/api";
import DetalheItem from "./DetalheItem";
import {
  BadgeSituacao, CarregandoSobre, ErroCarga, FILTROS_ITENS_PADRAO, Miniatura, Paginacao, SITUACOES_ABERTAS, TagTipo, Vazio,
  dataHoraBR, nomeConta, num, rotulo, useCelular,
} from "./comum";

const POR_PAGINA = 30;

const ORDENS = [
  { valor: "recentes", label: "Mais recentes" },
  { valor: "antigos", label: "Mais antigos" },
  { valor: "tentativas", label: "Mais tentativas" },
];

const TAMANHO_CAMPO = { base: "md", lg: "sm" };
const TAMANHO_ACAO = { base: "md", lg: "sm" };

const temFiltro = (f) => Object.keys(FILTROS_ITENS_PADRAO).some((k) => k !== "ordem" && f[k] !== FILTROS_ITENS_PADRAO[k]);

function CartaoItem({ i, rotulos, nomesContas, onAbrir }) {
  const abrir = () => onAbrir(i);
  return (
    <Box p={3} role="button" tabIndex={0} cursor="pointer" _hover={{ bg: "gray.50" }} _active={{ bg: "gray.100" }}
      _focusVisible={{ boxShadow: "outline", outline: "none" }} onClick={abrir}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          abrir();
        }
      }}>
      <Flex gap={3} align="flex-start">
        <Miniatura src={i.imagem_url} />
        <Box flex="1" minW={0}>
          {/* Situação longa desce para a linha de baixo em vez de cortar o MLB. */}
          <Flex justify="space-between" columnGap={2} rowGap={1} align="flex-start" flexWrap="wrap">
            <Text fontSize="sm" fontWeight="bold" noOfLines={1} minW={0}>{i.id_ml || "Sem anúncio"}</Text>
            <BadgeSituacao situacao={i.situacao} rotulos={rotulos} flexShrink={0} />
          </Flex>
          <Text fontSize="xs" color="gray.700" noOfLines={2}>
            {i.nome || (i.produto_id ? `Produto #${i.produto_id}` : "—")}
          </Text>
          <Text fontSize="2xs" color="gray.600" noOfLines={1}>
            {nomeConta(nomesContas, i.conta_id)}{i.codigo ? ` · ${i.codigo}` : ""}{i.fornecedor ? ` · ${i.fornecedor}` : ""}
          </Text>
          <Wrap spacing={1.5} mt={1.5} align="center">
            <WrapItem><TagTipo tipo={i.tipo} rotulos={rotulos} /></WrapItem>
            {i.motivo_codigo && (
              <WrapItem minW={0}><Text fontSize="xs" color="gray.700" noOfLines={1}>{rotulo(rotulos, "motivos", i.motivo_codigo)}</Text></WrapItem>
            )}
          </Wrap>
          <Text fontSize="xs" color="gray.600" mt={1} noOfLines={1}>
            {i.cura ? `→ ${rotulo(rotulos, "curas", i.cura)}` : "Sem diagnóstico ainda"}
            {i.tentativas > 0 ? ` · ${num(i.tentativas)} tentativa${Number(i.tentativas) === 1 ? "" : "s"}` : ""}
          </Text>
          {i.ultimo_erro && <Text fontSize="xs" color="red.600" noOfLines={1}>{i.ultimo_erro}</Text>}
        </Box>
      </Flex>
    </Box>
  );
}

export default function ItensTab({ rotulos, opcoes, config, isAdmin, filtros, onFiltros, onMudou }) {
  const celular = useCelular();
  // A dica longa não cabe ao lado do botão "Filtros" abaixo de 360 px (400 px com "Filtros (n)").
  const [largura360, largura400] = useMediaQuery(["(min-width: 360px)", "(min-width: 400px)"], { ssr: false });
  const [busca, setBusca] = useState(filtros.busca || "");
  const [pagina, setPagina] = useState(1);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(false);
  const [aberto, setAberto] = useState(null);
  const [verFiltros, setVerFiltros] = useState(false);
  const ultima = useRef(0);
  const topoLista = useRef(null);

  // Busca só dispara depois de uma pausa na digitação.
  useEffect(() => {
    const termo = busca.trim();
    if (termo === (filtros.busca || "")) return undefined;
    const t = setTimeout(() => {
      onFiltros((f) => ({ ...f, busca: termo }));
      setPagina(1);
    }, 400);
    return () => clearTimeout(t);
  }, [busca, filtros.busca, onFiltros]);

  const carregar = useCallback(async () => {
    const minha = ++ultima.current;
    setCarregando(true);
    const r = await getSaudeAnunciosItens({
      conta: filtros.conta || undefined,
      tipo: filtros.tipo || undefined,
      situacao: filtros.situacao || undefined,
      motivo: filtros.motivo || undefined,
      cura: filtros.cura || undefined,
      busca: filtros.busca || undefined,
      ordem: filtros.ordem || "recentes",
      pagina,
      por_pagina: POR_PAGINA,
    });
    if (minha !== ultima.current) return; // resposta atrasada de filtro antigo (ou a aba já fechou)
    setCarregando(false);
    if (r) {
      setDados(r);
      setErro(false);
    } else {
      setErro(true);
    }
  }, [filtros, pagina]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => () => { ultima.current += 1; }, []);

  // Página que ficou vazia (itens saíram do filtro): volta para a última que existe.
  useEffect(() => {
    if (!dados || (dados.itens || []).length > 0 || pagina <= 1 || !dados.total) return;
    const ultimaPagina = Math.max(1, Math.ceil(dados.total / (dados.por_pagina || POR_PAGINA)));
    if (ultimaPagina < pagina) setPagina(ultimaPagina);
  }, [dados, pagina]);

  const mudar = (campo, valor) => {
    onFiltros((f) => ({ ...f, [campo]: valor }));
    setPagina(1);
  };

  const limpar = () => {
    setBusca("");
    onFiltros({ ...FILTROS_ITENS_PADRAO, ordem: filtros.ordem });
    setPagina(1);
  };

  const aoMudar = () => {
    carregar();
    onMudou?.();
  };

  const itens = dados?.itens || [];
  const total = dados?.total || 0;
  const porPagina = dados?.por_pagina || POR_PAGINA;
  // contagens.situacao conta todas as situações (ignora o filtro de situação).
  const contSit = dados?.contagens?.situacao || null;
  const contTipo = dados?.contagens?.tipo || null;
  const somar = (lista) => (contSit ? lista.reduce((t, k) => t + (Number(contSit[k]) || 0), 0) : null);
  const filtrado = temFiltro(filtros);
  const qtdFiltros = ["conta", "tipo", "motivo", "cura"].filter((k) => filtros[k]).length;
  const dicaBusca = (qtdFiltros ? largura400 : largura360) ? "MLB ou código…" : "Buscar…";

  // Sem situação = em aberto (padrão do backend); "todas" inclui corrigidos, ignorados e resolvidos.
  const chips = [
    { valor: "", label: "Em aberto", n: somar(SITUACOES_ABERTAS) },
    { valor: "todas", label: "Todas", n: contSit ? somar(Object.keys(contSit)) : null },
    ...opcoes.situacoes
      .filter((s) => !contSit || Number(contSit[s]) > 0 || filtros.situacao === s)
      .map((s) => ({ valor: s, label: rotulo(rotulos, "situacoes", s), n: contSit ? Number(contSit[s]) || 0 : null })),
  ];

  return (
    <Box p={{ base: 3, md: 4 }}>
      <Flex gap={2} mb={3} flexWrap="wrap" role="group" aria-label="Situação">
        {chips.map((s) => {
          const sel = filtros.situacao === s.valor;
          return (
            <Button key={s.valor || "abertos"} size={{ base: "sm", lg: "xs" }} minH={{ base: "40px", lg: "auto" }} flexShrink={0} aria-pressed={sel}
              variant={sel ? "solid" : "outline"} colorScheme={sel ? "orange" : "gray"} bg={sel ? undefined : "white"}
              onClick={() => mudar("situacao", s.valor)}>
              {s.label}{s.n !== null ? ` (${num(s.n)})` : ""}
            </Button>
          );
        })}
      </Flex>

      {/* No celular os filtros ficam recolhidos atrás do botão "Filtros" (largura cheia, sem texto cortado). */}
      <Grid templateColumns={{ base: "minmax(0, 1fr)", md: "repeat(3, minmax(0, 1fr))", xl: "minmax(0, 1.6fr) repeat(5, minmax(0, 1fr))" }}
        gap={2} mb={2}>
        <Flex gap={2} minW={0}>
          <InputGroup size={TAMANHO_CAMPO} flex="1" minW={0}>
            <InputLeftElement pointerEvents="none"><Search size={14} color="#718096" aria-hidden /></InputLeftElement>
            <Input type="search" bg="white" placeholder={dicaBusca} aria-label="Buscar anúncio (MLB, id, código ou nome)"
              value={busca} onChange={(e) => setBusca(e.target.value)} />
          </InputGroup>
          <Button display={{ base: "inline-flex", md: "none" }} flexShrink={0} variant="outline" bg="white"
            leftIcon={<SlidersHorizontal size={16} />} onClick={() => setVerFiltros((v) => !v)}
            aria-expanded={verFiltros} aria-controls="saude-itens-filtros">
            Filtros{qtdFiltros ? ` (${qtdFiltros})` : ""}
          </Button>
        </Flex>
        <Box id="saude-itens-filtros" display={{ base: verFiltros ? "contents" : "none", md: "contents" }}>
          <Select size={TAMANHO_CAMPO} bg="white" aria-label="Conta" value={filtros.conta} onChange={(e) => mudar("conta", e.target.value)}>
            <option value="">Todas as contas</option>
            {opcoes.contas.map((c) => <option key={c.id} value={String(c.id)}>{c.nome}</option>)}
          </Select>
          <Select size={TAMANHO_CAMPO} bg="white" aria-label="Tipo de problema" value={filtros.tipo} onChange={(e) => mudar("tipo", e.target.value)}>
            <option value="">Todos os tipos</option>
            {opcoes.tipos.map((t) => (
              <option key={t} value={t}>
                {rotulo(rotulos, "tipos", t)}{contTipo && contTipo[t] !== undefined ? ` (${num(contTipo[t])})` : ""}
              </option>
            ))}
          </Select>
          <Select size={TAMANHO_CAMPO} bg="white" aria-label="Motivo do Mercado Livre" value={filtros.motivo} onChange={(e) => mudar("motivo", e.target.value)}>
            <option value="">Todos os motivos</option>
            {filtros.motivo && !opcoes.motivos.includes(filtros.motivo) && (
              <option value={filtros.motivo}>{rotulo(rotulos, "motivos", filtros.motivo)}</option>
            )}
            {opcoes.motivos.map((m) => <option key={m} value={m}>{rotulo(rotulos, "motivos", m)}</option>)}
          </Select>
          <Select size={TAMANHO_CAMPO} bg="white" aria-label="Cura" value={filtros.cura} onChange={(e) => mudar("cura", e.target.value)}>
            <option value="">Todas as curas</option>
            {opcoes.curas.map((c) => <option key={c} value={c}>{rotulo(rotulos, "curas", c)}</option>)}
          </Select>
          <Select size={TAMANHO_CAMPO} bg="white" aria-label="Ordem" value={filtros.ordem} onChange={(e) => mudar("ordem", e.target.value)}>
            {ORDENS.map((o) => <option key={o.valor} value={o.valor}>{o.label}</option>)}
          </Select>
        </Box>
      </Grid>

      <Flex justify="space-between" align="center" mb={2} minH="28px" gap={2} flexWrap="wrap">
        <Text fontSize="xs" color="gray.600" aria-live="polite">
          {dados ? `${num(total)} anúncio${total === 1 ? "" : "s"}` : ""}
        </Text>
        <HStack spacing={3}>
          {filtrado && (
            <Button size={TAMANHO_ACAO} variant="ghost" colorScheme="blue" leftIcon={<X size={12} />} onClick={limpar}>Limpar filtros</Button>
          )}
          <Button size={TAMANHO_ACAO} variant="ghost" leftIcon={<RefreshCw size={14} />} onClick={carregar} isLoading={carregando && !!dados}>
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {erro && dados && (
        <Alert status="warning" mb={3} borderRadius="md" fontSize="sm">
          <AlertIcon />Não foi possível atualizar a lista. Mostrando a última leitura.
        </Alert>
      )}

      {erro && !dados ? (
        <ErroCarga titulo="Não foi possível carregar os anúncios." onTentar={carregar} carregando={carregando} />
      ) : (
        <Box ref={topoLista} scrollMarginTop="80px" bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.100"
          position="relative" minH="120px" aria-busy={carregando}>
          {carregando && <CarregandoSobre />}

          {!carregando && dados && itens.length === 0 && (
            <Vazio
              titulo={filtrado ? "Nenhum anúncio com esses filtros" : "Nenhum anúncio em aberto"}
              acao={filtrado && <Button size={TAMANHO_ACAO} variant="outline" onClick={limpar}>Limpar filtros</Button>}
            >
              {filtrado
                ? "Tente outra situação ou tire algum filtro."
                : "Os anúncios com problema aparecem aqui depois da varredura. Os já resolvidos ficam em “Todas”."}
            </Vazio>
          )}

          {itens.length > 0 && celular && (
            <VStack align="stretch" spacing={0} divider={<Box borderBottom="1px solid" borderColor="gray.100" />}>
              {itens.map((i) => <CartaoItem key={i.id} i={i} rotulos={rotulos} nomesContas={opcoes.nomesContas} onAbrir={setAberto} />)}
            </VStack>
          )}

          {itens.length > 0 && !celular && (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Anúncio</Th>
                    <Th>Tipo</Th>
                    <Th>Motivo</Th>
                    <Th>Cura</Th>
                    <Th>Situação</Th>
                    <Th isNumeric>Tent.</Th>
                    <Th>Visto</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {itens.map((i) => (
                    <Tr key={i.id} _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => setAberto(i)}>
                      <Td minW={{ lg: "165px", xl: "240px" }} maxW="360px">
                        <HStack spacing={3} align="flex-start">
                          <Miniatura src={i.imagem_url} />
                          <Box minW={0}>
                            <Button variant="link" size="sm" colorScheme="blue" fontWeight="bold"
                              onClick={(e) => { e.stopPropagation(); setAberto(i); }}>
                              {i.id_ml || "Sem anúncio"}
                            </Button>
                            <Text fontSize="xs" color="gray.700" noOfLines={1}>{i.nome || (i.produto_id ? `Produto #${i.produto_id}` : "—")}</Text>
                            <Text fontSize="2xs" color="gray.600" noOfLines={1}>
                              {nomeConta(opcoes.nomesContas, i.conta_id)}{i.codigo ? ` · ${i.codigo}` : ""}{i.fornecedor ? ` · ${i.fornecedor}` : ""}
                            </Text>
                          </Box>
                        </HStack>
                      </Td>
                      <Td><TagTipo tipo={i.tipo} rotulos={rotulos} /></Td>
                      <Td maxW="220px"><Text fontSize="xs" noOfLines={2}>{rotulo(rotulos, "motivos", i.motivo_codigo)}</Text></Td>
                      <Td maxW="200px">
                        {i.cura
                          ? <Text fontSize="xs" noOfLines={2}>{rotulo(rotulos, "curas", i.cura)}</Text>
                          : <Text fontSize="xs" color="gray.600">Sem diagnóstico ainda</Text>}
                      </Td>
                      <Td>
                        <BadgeSituacao situacao={i.situacao} rotulos={rotulos} />
                        {i.ultimo_erro && (
                          <Tooltip label={i.ultimo_erro} hasArrow>
                            <Text fontSize="2xs" color="red.600" mt={1} tabIndex={0} w="fit-content">com erro</Text>
                          </Tooltip>
                        )}
                      </Td>
                      <Td isNumeric>{num(i.tentativas)}</Td>
                      <Td fontSize="xs" whiteSpace="nowrap">{dataHoraBR(i.visto_em)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      )}

      <Paginacao pagina={pagina} porPagina={porPagina} total={total} carregando={carregando} onPagina={setPagina}
        rolarPara={topoLista} />

      <DetalheItem item={aberto} rotulos={rotulos} config={config} isAdmin={isAdmin} nomesContas={opcoes.nomesContas}
        onFechar={() => setAberto(null)} onMudou={aoMudar} />
    </Box>
  );
}
